/** Scheduled ingestion, from the browser's side - ADR-0123 (COL-04).
 *
 * Two things this file is careful about.
 *
 * **A failed read is not "nothing is connected".** The Sources banner renders *nothing* for a
 * workspace with no feed, and that is a claim - so a request that failed must not be allowed to
 * produce it (EXP-29). `useConnectors` therefore throws on a failure, and the panel says the status
 * could not be read.
 *
 * **There is no "run now".** A run is the schedule's; the browser can create a connector, renew its
 * credential and withdraw it, and it reads the state that results. A credential is sent as a *name*
 * the person's own secrets store resolves - never a secret.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type Connector = components["schemas"]["ConnectorOut"];
export type ConnectorRun = components["schemas"]["RunOut"];

export const ingestionKeys = {
  all: ["ingestion"] as const,
  connectors: () => ["ingestion", "connectors"] as const,
};

/** Every connector in this workspace. Disabled for a role the server refuses (an Auditor), so the
 * page never makes a request that is certain to be a 403. */
export function useConnectors(enabled: boolean) {
  return useQuery({
    queryKey: ingestionKeys.connectors(),
    enabled,
    queryFn: async (): Promise<Connector[]> => {
      const { data, error, response } = await api.GET("/api/v1/ingestion/connectors", {});
      if (error || !data) {
        throw refusalFrom(response, error, "The status of the connected feeds could not be read.");
      }
      return data;
    },
  });
}

export interface RenewInput {
  connectorId: string;
  secretRef: string;
  /** A calendar date, `YYYY-MM-DD`: the last day the credential is good for. */
  expiresOn: string;
}

/** A person re-establishes the credential. The checkpoint is untouched; the next run resumes. */
export function useRenewCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ connectorId, secretRef, expiresOn }: RenewInput): Promise<Connector> => {
      const { data, error, response } = await api.POST(
        "/api/v1/ingestion/connectors/{connector_id}/credential",
        {
          params: { path: { connector_id: connectorId } },
          // The end of that day, in UTC, so "expires on the 30th" does not stop it on the 29th.
          body: { secret_ref: secretRef.trim(), expires_at: `${expiresOn}T23:59:59Z` },
        },
      );
      if (error || !data) throw refusalFrom(response, error, "The credential could not be renewed.");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ingestionKeys.all }),
  });
}
